// src/hooks/useMediaUpload.js

import { useState } from 'react';
import { supabase } from 'src/lib/supabase';

export const useMediaUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const reset = () => {
    setUploading(false);
    setProgress(0);
    setError(null);
  };

  const upload = async (file, { bucket, entityType, entityId, altText }) => {
    // Client-side validation
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'video/webm'];
    
    if (!file) {
      setError('No file provided');
      return null;
    }
    
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Allowed: jpg, png, webp, mp4, webm');
      return null;
    }
    
    const isImage = file.type.startsWith('image/');
    const fileMaxSize = isImage ? 5 * 1024 * 1024 : 50 * 1024 * 1024;
    
    if (file.size > fileMaxSize) {
      setError(`File too large. Max ${isImage ? '5MB' : '50MB'} for ${isImage ? 'images' : 'videos'}`);
      return null;
    }
    
    setUploading(true);
    setProgress(0);
    setError(null);
    
    try {
      // Try edge function first for images
      if (isImage) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('bucket', bucket);
          formData.append('entity_type', entityType);
          formData.append('entity_id', entityId);
          if (altText) formData.append('alt_text', altText);
          
          const response = await fetch(`${supabase.supabaseUrl}/functions/v1/optimize-image`, {
            method: 'POST',
            body: formData,
            headers: {
              'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
            }
          });
          
          if (response.ok) {
            const result = await response.json();
            setProgress(100);
            return result;
          }
        } catch (edgeError) {
          console.warn('Edge function failed, falling back to direct upload:', edgeError);
        }
      }
      
      // Fallback to direct upload
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${bucket}/${entityType}/${entityId}/${fileName}`;
      
      // Progress simulation
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 200);
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      clearInterval(progressInterval);
      
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(filePath);

      // Insert into media table — real columns are original_name, file_size,
      // mime_type, urls (jsonb), metadata (jsonb). There are no flat
      // url/path/bucket/type/size/alt_text/versions columns.
      const { data: media, error: insertError } = await supabase
        .from('media')
        .insert({
          original_name: file.name,
          file_size: file.size,
          mime_type: file.type,
          urls: { original: publicUrl },
          metadata: {
            bucket,
            path: filePath,
            type: isImage ? 'image' : 'video',
            alt_text: altText || file.name,
          },
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Insert media link — owner_id is required (not-null constraint),
      // so we need the currently authenticated user.
      if (entityId && entityType && entityType !== 'temp') {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('media_links')
            .insert({
              media_id: media.id,
              owner_id: user.id,
              entity_type: entityType,
              entity_id: entityId,
            });
        }
      }

      setProgress(100);
      return { mediaId: media.id, versions: { original: publicUrl } };
      
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed. Please try again.');
      return null;
    } finally {
      setUploading(false);
    }
  };
  
  return { upload, uploading, progress, error, reset };
};